import { Fragment, useState, useEffect } from "react";

// react-router components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import MuiLink from "@mui/material/Link";

import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "../../hooks/useLocalizedNavigate";

import PointieBox from "../components/PointieBox";
import PointieTypography from "../components/PointieTypography";
import PointieButton from "../components/PointieButton";

import DefaultNavbarDropdown from "./DefaultNavbarDropdown";
import DefaultNavbarMobile from "./DefaultNavbarMobile";
import LanguageSwitcher from "../components/LanguageSwitcher";

import breakpoints from "../../assets/theme/base/breakpoints";

import logoCT from "../../assets/images/pointie_logo.png";

function DefaultNavbar({
  brand,
  routes,
  transparent,
  light,
  action,
  actions,
  sticky,
  relative,
  center,
}) {
  const { t } = useTranslation(["common", "nav"]);
  const lp = useLocalizedPath();

  // Translate route name if nameKey exists
  const tn = (item) => (item.nameKey ? t(item.nameKey) : item.name);

  const brandContent = brand || (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img
        src={logoCT}
        width="15"
        height="15"
        style={{ width: 15, marginRight: 5 }}
        alt="Let's Pointie"
      />
      <span style={{ fontSize: 15 }}>{t("brand")}</span>
    </div>
  );

  const [dropdown, setDropdown] = useState("");
  const [dropdownEl, setDropdownEl] = useState("");
  const [dropdownName, setDropdownName] = useState("");
  const [nestedDropdown, setNestedDropdown] = useState("");
  const [nestedDropdownEl, setNestedDropdownEl] = useState("");
  const [nestedDropdownName, setNestedDropdownName] = useState("");
  const [arrowRef, setArrowRef] = useState(null);
  const [mobileNavbar, setMobileNavbar] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const openMobileNavbar = () => setMobileNavbar(!mobileNavbar);

  useEffect(() => {
    // A function that sets the display state for the DefaultNavbarMobile.
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.lg) {
        setMobileView(true);
        setMobileNavbar(false);
      } else {
        setMobileView(false);
        setMobileNavbar(false);
      }
    }

    /**
     The event listener that's calling the displayMobileNavbar function when
     resizing the window.
    */
    window.addEventListener("resize", displayMobileNavbar);

    // Call the displayMobileNavbar function to set the state with the initial value.
    displayMobileNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", displayMobileNavbar);
  }, []);

  const renderNavbarItems = routes.map(({ name, nameKey, icon, href, route, collapse }) => (
    <DefaultNavbarDropdown
      key={name}
      name={name}
      nameKey={nameKey}
      icon={icon}
      href={href}
      route={route}
      collapse={Boolean(collapse)}
      onMouseEnter={({ currentTarget }) => {
        if (collapse) {
          setDropdown(currentTarget);
          setDropdownEl(currentTarget);
          setDropdownName(name);
        }
      }}
      onMouseLeave={() => collapse && setDropdown(null)}
      light={light}
    />
  ));

  // Render the routes on the dropdown menu
  const renderRoutes = routes.map(({ name, collapse, columns, rowsPerColumn }) => {
    let template;

    // Render the dropdown menu that should be display as columns
    if (collapse && columns && name === dropdownName) {
      const calculateColumns = collapse.reduce((resultArray, item, index) => {
        const chunkIndex = Math.floor(index / rowsPerColumn);

        if (!resultArray[chunkIndex]) {
          resultArray[chunkIndex] = [];
        }

        resultArray[chunkIndex].push(item);

        return resultArray;
      }, []);

      template = (
        <Grid key={name} container spacing={3} py={1} px={1.5}>
          {calculateColumns.map((cols, key) => {
            const gridKey = `grid-${key}`;
            const dividerKey = `divider-${key}`;

            return (
              <Grid key={gridKey} item xs={12 / columns} sx={{ position: "relative" }}>
                {cols.map((col, index) => (
                  <Fragment key={col.name}>
                    <PointieTypography
                      display="block"
                      variant="button"
                      fontWeight="bold"
                      textTransform="capitalize"
                      py={1}
                      px={0.5}
                      mt={index !== 0 ? 2 : 0}
                    >
                      {tn(col)}
                    </PointieTypography>
                    {col.collapse.map((item) => (
                      <PointieTypography
                        key={item.name}
                        component={item.route ? Link : MuiLink}
                        to={item.route ? lp(`/${item.route}`) : ""}
                        href={item.href ? item.href : (e) => e.preventDefault()}
                        target={item.href ? "_blank" : ""}
                        rel={item.href ? "noreferrer" : "noreferrer"}
                        minWidth="11.25rem"
                        display="block"
                        variant="button"
                        color="text"
                        textTransform="capitalize"
                        fontWeight="regular"
                        py={0.625}
                        px={2}
                        sx={({ palette: { grey } }) => ({
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 300ms linear",

                          "&:hover": {
                            backgroundColor: grey[200],
                            color: "#344767",
                          },
                        })}
                      >
                        {tn(item)}
                      </PointieTypography>
                    ))}
                  </Fragment>
                ))}
                {key !== 0 && (
                  <Divider
                    key={dividerKey}
                    orientation="vertical"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "-4px",
                      transform: "translateY(-45%)",
                      height: "90%",
                    }}
                  />
                )}
              </Grid>
            );
          })}
        </Grid>
      );

      // Render the dropdown menu that should be display as list items
    } else if (collapse && name === dropdownName) {
      template = collapse.map((item) => {
        const linkComponent = {
          component: MuiLink,
          href: item.href,
          target: "_blank",
          rel: "noreferrer",
        };

        const routeComponent = {
          component: Link,
          to: lp(`/${item.route}`),
        };

        return (
          <PointieTypography
            key={item.name}
            {...(item.route ? routeComponent : linkComponent)}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            variant="button"
            textTransform="capitalize"
            minWidth={item.description ? "14rem" : "12rem"}
            color={item.description ? "dark" : "text"}
            fontWeight={item.description ? "bold" : "regular"}
            py={item.description ? 1 : 0.625}
            px={2}
            sx={({ palette: { grey } }) => ({
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 300ms linear",

              "&:hover": {
                backgroundColor: grey[200],
                color: "#344767",

                "& *": {
                  color: "#344767",
                },
              },
            })}
            onMouseEnter={({ currentTarget }) => {
              if (item.dropdown) {
                setNestedDropdown(currentTarget);
                setNestedDropdownEl(currentTarget);
                setNestedDropdownName(item.name);
              }
            }}
            onMouseLeave={() => {
              if (item.dropdown) {
                setNestedDropdown(null);
              }
            }}
          >
            {item.description ? (
              <PointieBox>
                {tn(item)}
                <PointieTypography
                  display="block"
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ transition: "all 300ms linear" }}
                >
                  {item.description}
                </PointieTypography>
              </PointieBox>
            ) : (
              item.name
            )}
            {item.collapse && (
              <Icon
                fontSize="small"
                sx={{ fontWeight: "normal", verticalAlign: "middle", mr: -0.5 }}
              >
                keyboard_arrow_right
              </Icon>
            )}
          </PointieTypography>
        );
      });
    }

    return template;
  });

  // Routes dropdown menu
  const dropdownMenu = (
    <Popper
      anchorEl={dropdown}
      popperRef={null}
      open={Boolean(dropdown)}
      placement="top-start"
      transition
      style={{ zIndex: 10 }}
      modifiers={[
        {
          name: "arrow",
          enabled: true,
          options: {
            element: arrowRef,
          },
        },
      ]}
      onMouseEnter={() => setDropdown(dropdownEl)}
      onMouseLeave={() => {
        if (!nestedDropdown) {
          setDropdown(null);
          setDropdownName("");
        }
      }}
    >
      {({ TransitionProps }) => (
        <Grow
          {...TransitionProps}
          sx={{
            transformOrigin: "left top",
            background: ({ palette: { white } }) => white.main,
          }}
        >
          <PointieBox borderRadius="lg">
            <PointieTypography variant="h1" component="span" color="white">
              <Icon ref={setArrowRef} sx={{ mt: -3 }}>
                arrow_drop_up
              </Icon>
            </PointieTypography>
            <PointieBox shadow="lg" borderRadius="lg" p={2} mt={2}>
              {renderRoutes}
            </PointieBox>
          </PointieBox>
        </Grow>
      )}
    </Popper>
  );

  // Render routes that are nested inside the dropdown menu routes
  const renderNestedRoutes = routes.map(({ collapse, columns }) =>
    collapse && !columns
      ? collapse.map(({ name: parentName, collapse: nestedCollapse }) => {
          let template;

          if (parentName === nestedDropdownName) {
            template =
              nestedCollapse &&
              nestedCollapse.map((item) => {
                const linkComponent = {
                  component: MuiLink,
                  href: item.href,
                  target: "_blank",
                  rel: "noreferrer",
                };

                const routeComponent = {
                  component: Link,
                  to: lp(`/${item.route}`),
                };

                return (
                  <PointieTypography
                    key={item.name}
                    {...(item.route ? routeComponent : linkComponent)}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    variant="button"
                    textTransform="capitalize"
                    minWidth={item.description ? "14rem" : "12rem"}
                    color={item.description ? "dark" : "text"}
                    fontWeight={item.description ? "bold" : "regular"}
                    py={item.description ? 1 : 0.625}
                    px={2}
                    sx={({ palette: { grey } }) => ({
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 300ms linear",

                      "&:hover": {
                        backgroundColor: grey[200],
                        color: "#344767",

                        "& *": {
                          color: "#344767",
                        },
                      },
                    })}
                  >
                    {item.description ? (
                      <PointieBox>
                        {tn(item)}
                        <PointieTypography
                          display="block"
                          variant="button"
                          color="text"
                          fontWeight="regular"
                          sx={{ transition: "all 300ms linear" }}
                        >
                          {item.description}
                        </PointieTypography>
                      </PointieBox>
                    ) : (
                      item.name
                    )}
                    {item.collapse && (
                      <Icon
                        fontSize="small"
                        sx={{ fontWeight: "normal", verticalAlign: "middle", mr: -0.5 }}
                      >
                        keyboard_arrow_right
                      </Icon>
                    )}
                  </PointieTypography>
                );
              });
          }

          return template;
        })
      : null
  );

  // Dropdown menu for the nested dropdowns
  const nestedDropdownMenu = (
    <Popper
      anchorEl={nestedDropdown}
      popperRef={null}
      open={Boolean(nestedDropdown)}
      placement="right-start"
      transition
      style={{ zIndex: 10 }}
      onMouseEnter={() => {
        setNestedDropdown(nestedDropdownEl);
      }}
      onMouseLeave={() => {
        setNestedDropdown(null);
        setNestedDropdownName("");
        setDropdown(null);
      }}
    >
      {({ TransitionProps }) => (
        <Grow
          {...TransitionProps}
          sx={{
            transformOrigin: "left top",
            background: ({ palette: { white } }) => white.main,
          }}
        >
          <PointieBox ml={2.5} mt={-2.5} borderRadius="lg">
            <PointieBox shadow="lg" borderRadius="lg" py={1.5} px={1} mt={2}>
              {renderNestedRoutes}
            </PointieBox>
          </PointieBox>
        </Grow>
      )}
    </Popper>
  );

  return (
    <Container sx={sticky ? { position: "sticky", top: 0, zIndex: 10 } : null}>
      <PointieBox
        py={1}
        px={{ xs: 4, sm: transparent ? 2 : 3, lg: transparent ? 0 : 2 }}
        my={relative ? 0 : 2}
        mx={relative ? 0 : 3}
        width={relative ? "100%" : "calc(100% - 48px)"}
        borderRadius="xl"
        shadow={transparent ? "none" : "md"}
        color={light ? "white" : "dark"}
        position={relative ? "relative" : "absolute"}
        left={0}
        zIndex={50}
        sx={{
          backgroundColor: transparent ? "transparent" : "rgba(255, 255, 255, 0.8)",
          backdropFilter: transparent ? "none" : "saturate(200%) blur(30px)",
        }}
      >
        <PointieBox display="flex" justifyContent="space-between" alignItems="center">
          <PointieBox
            component={Link}
            to={lp("/")}
            lineHeight={1}
            py={transparent ? 1.5 : 0.75}
            pl={relative || transparent ? 0 : { xs: 0, lg: 1 }}
            sx={{ textDecoration: "none" }}
          >
            <PointieTypography
              variant="body2"
              fontWeight="bold"
              color={light ? "white" : "dark"}
              sx={{ fontSize: "0.95rem" }}
            >
              {brandContent}
            </PointieTypography>
          </PointieBox>
          <PointieBox
            color="inherit"
            display={{ xs: "none", lg: "flex" }}
            ml="auto"
            mr={center ? "auto" : 0}
          >
            {renderNavbarItems}
          </PointieBox>
          <PointieBox
            ml={{ xs: "auto", lg: 0 }}
            display={{ xs: "none", lg: "flex" }}
            gap={1}
            alignItems="center"
          >
            <LanguageSwitcher />
            {actions &&
              actions.map((act, idx) =>
                act.type === "internal" ? (
                  <PointieButton
                    key={idx}
                    component={Link}
                    to={lp(act.route)}
                    variant={
                      act.color === "white" || act.color === "default" ? "contained" : "gradient"
                    }
                    color={act.color ? act.color : "info"}
                    size="small"
                    startIcon={
                      act.icon ? <Icon sx={{ fontSize: "16px !important" }}>{act.icon}</Icon> : null
                    }
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {act.labelKey ? t(act.labelKey) : act.label}
                  </PointieButton>
                ) : (
                  <PointieButton
                    key={idx}
                    component="a"
                    href={act.route}
                    target="_blank"
                    rel="noreferrer"
                    variant={
                      act.color === "white" || act.color === "default" ? "contained" : "gradient"
                    }
                    color={act.color ? act.color : "info"}
                    size="small"
                    startIcon={
                      act.icon ? <Icon sx={{ fontSize: "16px !important" }}>{act.icon}</Icon> : null
                    }
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {act.labelKey ? t(act.labelKey) : act.label}
                  </PointieButton>
                )
              )}
            {!actions &&
              action &&
              (action.type === "internal" ? (
                <PointieButton
                  component={Link}
                  to={lp(action.route)}
                  variant={
                    action.color === "white" || action.color === "default"
                      ? "contained"
                      : "gradient"
                  }
                  color={action.color ? action.color : "info"}
                  size="small"
                  startIcon={
                    action.icon ? (
                      <Icon sx={{ fontSize: "16px !important" }}>{action.icon}</Icon>
                    ) : null
                  }
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  {action.labelKey ? t(action.labelKey) : action.label}
                </PointieButton>
              ) : (
                <PointieButton
                  component="a"
                  href={action.route}
                  target="_blank"
                  rel="noreferrer"
                  variant={
                    action.color === "white" || action.color === "default"
                      ? "contained"
                      : "gradient"
                  }
                  color={action.color ? action.color : "info"}
                  size="small"
                  startIcon={
                    action.icon ? (
                      <Icon sx={{ fontSize: "16px !important" }}>{action.icon}</Icon>
                    ) : null
                  }
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  {action.labelKey ? t(action.labelKey) : action.label}
                </PointieButton>
              ))}
          </PointieBox>
          <PointieBox display={{ xs: "flex", lg: "none" }} alignItems="center" gap={1} ml="auto">
            <LanguageSwitcher />
            <PointieBox
              lineHeight={0}
              py={1.5}
              pl={1.5}
              color={transparent ? "white" : "inherit"}
              sx={{ cursor: "pointer" }}
              onClick={openMobileNavbar}
            >
              <Icon fontSize="default">{mobileNavbar ? "close" : "menu"}</Icon>
            </PointieBox>
          </PointieBox>
        </PointieBox>
        <PointieBox
          bgColor={transparent ? "white" : "transparent"}
          shadow={transparent ? "lg" : "none"}
          borderRadius="xl"
          px={transparent ? 2 : 0}
        >
          {mobileView && (
            <DefaultNavbarMobile routes={routes} open={mobileNavbar} actions={actions} />
          )}
        </PointieBox>
      </PointieBox>
      {dropdownMenu}
      {nestedDropdownMenu}
    </Container>
  );
}

// Setting default values for the props of DefaultNavbar
DefaultNavbar.defaultProps = {
  brand: null,
  transparent: false,
  light: false,
  action: false,
  sticky: false,
  relative: false,
  center: false,
};

// Typechecking props for the DefaultNavbar
DefaultNavbar.propTypes = {
  brand: PropTypes.node,
  routes: PropTypes.arrayOf(PropTypes.shape).isRequired,
  transparent: PropTypes.bool,
  light: PropTypes.bool,
  action: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal"]).isRequired,
      route: PropTypes.string.isRequired,
      color: PropTypes.oneOf([
        "primary",
        "secondary",
        "info",
        "success",
        "warning",
        "error",
        "dark",
        "light",
        "default",
        "white",
      ]),
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
    }),
  ]),
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal"]).isRequired,
      route: PropTypes.string.isRequired,
      color: PropTypes.string,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
    })
  ),
  sticky: PropTypes.bool,
  relative: PropTypes.bool,
  center: PropTypes.bool,
};

export default DefaultNavbar;
